import { clamp } from "@/utils/math"
import { vec2, vec3, vec4 } from "gl-matrix"

export function XYtoXYZ([x, y]: vec2): vec3 {
	// prettier-ignore
	return [
		x / y,
		1.0,
		(1 - x - y) / y
	]
}

export function XYZtoXY([X, Y, Z]: vec3): vec2 {
	// prettier-ignore
	return [
		X / (X + Y + Z),
		Y / (X + Y + Z)
	]
}

export function UVtoXY([u, v]: vec2): vec2 {
	v *= 1.5

	// prettier-ignore
	return [
		9 * u / (6 * u - 16 * v + 12),
		2 * v / (3 * u - 8 * v + 6)
	]
}

export function XYtoUV([x, y]: vec2): vec2 {
	const denom = -2 * x + 12 * y + 3

	// prettier-ignore
	return [
		4.0 * x / denom,
		6.0 * y / denom
	]
}

export function RGBToHSV([r, g, b]: [number, number, number]): [number, number, number] {
	const max: number = Math.max(r, g, b) // Find the maximum value among R, G, B
	const min: number = Math.min(r, g, b) // Find the minimum value among R, G, B
	let h: number = 0
	let s: number = 0
	let v: number = max // The value is set to the maximum value

	const d = max - min // Difference between max and min
	s = max === 0 ? 0 : d / max // Saturation

	if (max !== min) {
		// Calculate hue depending on which component is the max value
		switch (max) {
			case r:
				h = (g - b) / d + (g < b ? 6 : 0)
				break
			case g:
				h = (b - r) / d + 2
				break
			case b:
				h = (r - g) / d + 4
				break
		}
		h /= 6
		h *= 360 // Convert hue to degrees
	}

	s *= 100 // Convert saturation to percentage
	v *= 100 // Convert value to percentage

	return [h, s, v]
}

// input hsv in [0, 1] for this function; output RGB is always in [0, 1]
export function hsvToRGB([h, s, v]: vec3): vec3 {
	let r: number, g: number, b: number
	h = clamp(h, 0, 1)
	s = clamp(s, 0, 1)
	v = clamp(v, 0, 1)

	const i = Math.floor(h * 6)
	const f = h * 6 - i
	const p = v * (1 - s)
	const q = v * (1 - f * s)
	const t = v * (1 - (1 - f) * s)

	switch ((i % 6) as 0 | 1 | 2 | 3 | 4 | 5) {
		case 0:
			r = v
			g = t
			b = p
			break
		case 1:
			r = q
			g = v
			b = p
			break
		case 2:
			r = p
			g = v
			b = t
			break
		case 3:
			r = p
			g = q
			b = v
			break
		case 4:
			r = t
			g = p
			b = v
			break
		case 5:
			r = v
			g = p
			b = q
			break
	}

	return [r, g, b]
}

// input HSV is in 0-100 (0-360 for hue)
export function HSVToRGB([h, s, v]: vec3): vec3 {
	return hsvToRGB([h / 360, s / 100, v / 100])
}

export function RGBToHSL([r, g, b]: [number, number, number]): [number, number, number] {
	const max: number = Math.max(r, g, b)
	const min: number = Math.min(r, g, b)
	let h: number = 0
	let s: number = 0
	let l: number = (max + min) / 2

	if (max !== min) {
		const d = max - min
		s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
		switch (max) {
			case r:
				h = (g - b) / d + (g < b ? 6 : 0)
				break
			case g:
				h = (b - r) / d + 2
				break
			case b:
				h = (r - g) / d + 4
				break
		}
		h /= 6
		h *= 360
	}

	s *= 100
	l *= 100
	return [h, s, l]
}

export function HSLToRGB([h, s, l]: [number, number, number]): [number, number, number] {
	s /= 100
	l /= 100
	const c = (1 - Math.abs(2 * l - 1)) * s
	const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
	const m = l - c / 2
	let r = 0,
		g = 0,
		b = 0

	if (h >= 0 && h < 60) {
		r = c + m
		g = x + m
		b = m
	} else if (h >= 60 && h < 120) {
		r = x + m
		g = c + m
		b = m
	} else if (h >= 120 && h < 180) {
		r = m
		g = c + m
		b = x + m
	} else if (h >= 180 && h < 240) {
		r = m
		g = x + m
		b = c + m
	} else if (h >= 240 && h < 300) {
		r = x + m
		g = m
		b = c + m
	} else if (h >= 300 && h < 360) {
		r = c + m
		g = m
		b = x + m
	}

	return [r, g, b]
}

export function colorWheelToRGBY([h, s, l]: vec3): vec4 {
	const rgb: vec3 = hsvToRGB([h, s, 1.0])

	// normalize
	const avg = (rgb[0] + rgb[1] + rgb[2]) / 3
	const delta = 1 - avg

	rgb[0] = rgb[0] + delta // * (1 + l * 0.333)
	rgb[1] = rgb[1] + delta // * (1 + l * 0.333)
	rgb[2] = rgb[2] + delta // * (1 + l * 0.333)

	return [...rgb, l]
}
